import type { CSSProperties, JSX } from 'react';
import { Button, IconCheck, cn } from '@taki/ui';
import type { GoogleAgenda, GoogleAllDayEvent, GoogleWeekEvent } from './googleAgenda.js';

/**
 * L'icone du bouton Google Agenda : le trace fourni par le fondateur, recopie
 * ICI, en ligne. Meme recette que le glyphe de `AgendaPicker.tsx` : aucun
 * fichier `.svg` a cote, et la couleur vient du texte. Elle n'est PLUS posee
 * sur les evenements : c'est leur deuxieme ligne, « Google Agenda », qui les
 * distingue des creneaux Taki.
 */
export function ArchiveIcon({ size = 12, className }: { size?: number; className?: string }): JSX.Element {
  return (
    <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 239.01 221.61" fill="currentColor" className={className}>
      <path d="M239.01,0v69.71l-17.38.08-.05,151.82H17.43s-.05-151.81-.05-151.81l-17.38-.15V0s239.01,0,239.01,0ZM196.66,69.71H42.35v126.94h154.3v-126.94Z" />
      <rect x="89.63" y="97.07" width="59.75" height="24.95" />
    </svg>
  );
}

const FOCUS = 'focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border';

/** Connecter, ou deconnecter une fois connecte. Absent sans ID client. */
export function GoogleAgendaButton({ agenda }: { agenda: GoogleAgenda }): JSX.Element | null {
  if (!agenda.available) return null;
  if (agenda.connected) {
    return (
      <Button variant="secondary" size="sm" aria-pressed aria-label="Déconnecter Google Agenda" title="Déconnecter Google Agenda" onClick={agenda.disconnect} className={FOCUS}>
        <IconCheck size={14} className="shrink-0" />
        Google Agenda
      </Button>
    );
  }
  return (
    <Button variant="secondary" size="sm" disabled={agenda.busy} aria-label="Connecter Google Agenda" title="Afficher les événements de Google Agenda, en lecture seule" onClick={agenda.connect} className={FOCUS}>
      <ArchiveIcon size={14} className="shrink-0" />
      {agenda.busy ? 'Connexion…' : 'Google Agenda'}
    </Button>
  );
}

function timeLabel(value: number): string {
  const minutes = Math.round(value * 60);
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * Un evenement Google sur la grille. Un LIEN vers l'evenement dans Google
 * Agenda, jamais un creneau : on ne le deplace pas, on ne le redimensionne pas,
 * on ne l'edite pas ici. La position (top, height, left) vient de la grille.
 */
export function GoogleEventCard({ event, style }: { event: GoogleWeekEvent; style: CSSProperties }): JSX.Element {
  const range = `${timeLabel(event.start)} - ${timeLabel(event.start + event.duration)}`;
  return (
    <a
      href={event.link ?? undefined}
      target="_blank"
      rel="noreferrer"
      draggable={false}
      aria-label={`Événement Google Agenda : ${event.title}, ${range}`}
      title={`Google Agenda · ${event.title} · ${range}`}
      className="absolute inset-x-1.5 z-10 overflow-hidden rounded-sm border border-border-strong bg-surface-1 px-2 py-1.5 text-ink-900 no-underline transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border"
      style={style}
    >
      {/* Meme ossature que les creneaux Taki, la ligne client · projet devenant
          « Google Agenda ». Trop court pour trois lignes, l'horaire est coupe en
          premier : il reste lisible dans la position de la carte et au survol. */}
      {event.duration <= 0.5 ? (
        <>
          <p className="truncate text-[0.6875rem] font-semibold leading-4"><span className="mr-1 tabular-nums text-ink-700">{timeLabel(event.start)}</span>{event.title}</p>
          <p className="truncate text-[0.6875rem] leading-4 text-ink-500">Google Agenda</p>
        </>
      ) : (
        <>
          <p className={cn('text-xs font-semibold leading-4', event.duration <= 1 ? 'truncate' : 'line-clamp-2')}>{event.title}</p>
          <p className="mt-0.5 truncate text-[0.6875rem] text-ink-500">Google Agenda</p>
          <p className="mt-1 text-[0.6875rem] tabular-nums text-ink-700">{range}</p>
        </>
      )}
    </a>
  );
}

/**
 * Range les journees entieres en COULOIRS : un evenement prend le premier
 * couloir libre sur toute sa duree. Les plus longs passent d'abord, a debut
 * egal, pour que les petits se glissent a cote plutot que dessous.
 */
function laneOf(events: readonly GoogleAllDayEvent[], dayCount: number) {
  const laneEnds: number[] = [];
  return events
    .map((event) => ({ event, first: event.firstDay, last: Math.min(event.lastDay, dayCount - 1) }))
    .filter((item) => item.first <= item.last)
    .sort((a, b) => a.first - b.first || b.last - b.first - (a.last - a.first))
    .map((item) => {
      let lane = laneEnds.findIndex((end) => end < item.first);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(item.last);
      } else {
        laneEnds[lane] = item.last;
      }
      return { ...item, lane };
    });
}

/**
 * LA RANGEE JOURNEE ENTIERE, sous les jours de la semaine et collee avec eux
 * en haut de la grille. Memes colonnes que la grille horaire ; un evenement de
 * plusieurs jours s'etale sur ses jours. Sans evenement sur les jours AFFICHES
 * (week-end replie compris), elle n'existe pas : pas de bande vide.
 */
export function GoogleAllDayRow({ events, dayCount, columns }: {
  events: readonly GoogleAllDayEvent[];
  dayCount: number;
  columns: string;
}): JSX.Element | null {
  const placed = laneOf(events, dayCount);
  if (placed.length === 0) return null;
  const lanes = Math.max(...placed.map((item) => item.lane + 1));
  const span = `1 / span ${lanes}`;
  return (
    <div role="group" aria-label="Journées entières Google Agenda" className={cn('grid auto-rows-[2.375rem] border-t border-border-soft', columns)}>
      {Array.from({ length: dayCount }, (_, day) => (
        <div key={day} aria-hidden className="border-l border-border-soft" style={{ gridColumn: day + 2, gridRow: span }} />
      ))}
      {placed.map(({ event, first, last, lane }) => (
        <a
          key={`${event.id}-${first}`}
          href={event.link ?? undefined}
          target="_blank"
          rel="noreferrer"
          draggable={false}
          aria-label={`Journée entière Google Agenda : ${event.title}`}
          title={`Google Agenda · ${event.title} · jour entier`}
          className="z-[1] mx-1 my-0.5 flex min-w-0 flex-col justify-center rounded-sm border border-border-strong bg-surface-1 px-1.5 leading-4 no-underline transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border"
          style={{ gridColumn: `${first + 2} / ${last + 3}`, gridRow: lane + 1 }}
        >
          <span className="truncate text-[0.6875rem] font-semibold text-ink-900">{event.title}</span>
          <span className="truncate text-[0.625rem] text-ink-500">Google Agenda</span>
        </a>
      ))}
    </div>
  );
}
